import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { 
  Card, 
  Alert, 
  Button, 
  Descriptions, 
  Table, 
  Tag, 
  Space, 
  Divider,
  Collapse,
  Statistic,
  Row,
  Col,
  Typography,
  Spin,
  Empty,
  message
} from 'antd';
import { 
  CheckCircleOutlined, 
  ExclamationCircleOutlined, 
  InfoCircleOutlined,
  WarningOutlined,
  EyeOutlined,
  CalendarOutlined,
  ShopOutlined,
  ClockCircleOutlined
} from '@ant-design/icons';
import type { 
  PlanogramTemplate, 
  ValidationError, 
  ValidationResult,
  MilestoneConfig,
  PlanPreview
} from '../../../../types/plan-template';
import { 
  validateTemplate,
  validateMilestoneSequence,
  validateCycleConfiguration,
  validateScopeConfiguration
} from '../../../../utils/templateValidation';
import { PlanogramTemplateService } from '../../../../services/planogramTemplateService';
import dayjs from 'dayjs';

const { Title, Text, Paragraph } = Typography;
const { Panel } = Collapse;

interface ValidationPreviewProps {
  templateData: Partial<PlanogramTemplate>;
  onDataChange: (data: Partial<PlanogramTemplate>) => void;
  onValidationChange: (isValid: boolean, errors: ValidationError[]) => void;
  onNext?: () => void;
  onPrevious?: () => void;
}

interface ValidationSection {
  title: string;
  icon: React.ReactNode;
  status: 'success' | 'error' | 'warning' | 'info';
  errors: ValidationError[];
  warnings: ValidationError[];
  details?: React.ReactNode;
}

const ValidationPreview: React.FC<ValidationPreviewProps> = ({
  templateData,
  onDataChange,
  onValidationChange,
  onNext,
  onPrevious
}) => {
  const { t } = useTranslation();
  
  const [validationResult, setValidationResult] = useState<ValidationResult>({
    isValid: false,
    errors: [],
    warnings: []
  });
  const [validationSections, setValidationSections] = useState<ValidationSection[]>([]);
  const [planPreviews, setPlanPreviews] = useState<PlanPreview[]>([]);
  const [loading, setLoading] = useState(false);
  const [previewLoading, setPreviewLoading] = useState(false);

  // Perform comprehensive validation
  const performValidation = useCallback(async () => {
    setLoading(true);
    
    try {
      // Basic template validation
      const basicValidation = validateTemplate(templateData, t);
      
      // Milestone validation
      const milestoneValidation = templateData.milestones 
        ? validateMilestoneSequence(templateData.milestones, t)
        : { isValid: false, errors: [{ field: 'milestones', message: t('planTemplate.wizard.validationPreview.sections.milestone.configureRequired'), type: 'error' as const }], warnings: [] };
      
      // Cycle validation
      const cycleValidation = templateData.executionCycle
        ? validateCycleConfiguration(templateData.executionCycle, t)
        : { isValid: false, errors: [{ field: 'executionCycle', message: t('planTemplate.wizard.validationPreview.sections.cycle.configureRequired'), type: 'error' as const }], warnings: [] };
      
      // Scope validation
      const scopeValidation = templateData.scopeDefinition
        ? validateScopeConfiguration(templateData.scopeDefinition, t)
        : { isValid: false, errors: [{ field: 'scopeDefinition', message: t('planTemplate.wizard.validationPreview.sections.scope.configureRequired'), type: 'error' as const }], warnings: [] };

      // Combine all validation results
      const allErrors = [
        ...basicValidation.errors,
        ...milestoneValidation.errors,
        ...cycleValidation.errors,
        ...scopeValidation.errors
      ];

      const allWarnings = [
        ...(basicValidation.warnings || []),
        ...(milestoneValidation.warnings || []),
        ...(cycleValidation.warnings || []),
        ...(scopeValidation.warnings || [])
      ];

      const finalResult: ValidationResult = {
        isValid: allErrors.length === 0,
        errors: allErrors,
        warnings: allWarnings
      };

      setValidationResult(finalResult);
      onValidationChange(finalResult.isValid, finalResult.errors);

      // Create validation sections
      const sections: ValidationSection[] = [
        {
          title: t('planTemplate.wizard.validationPreview.sections.basicInfo.title'),
          icon: <InfoCircleOutlined />,
          status: getValidationStatus(basicValidation),
          errors: basicValidation.errors,
          warnings: basicValidation.warnings || [],
          details: renderBasicInfoDetails()
        },
        {
          title: t('planTemplate.wizard.validationPreview.sections.milestone.title'),
          icon: <ClockCircleOutlined />,
          status: getValidationStatus(milestoneValidation),
          errors: milestoneValidation.errors,
          warnings: milestoneValidation.warnings || [],
          details: renderMilestoneDetails()
        },
        {
          title: t('planTemplate.wizard.validationPreview.sections.cycle.title'),
          icon: <CalendarOutlined />,
          status: getValidationStatus(cycleValidation),
          errors: cycleValidation.errors,
          warnings: cycleValidation.warnings || [],
          details: renderCycleDetails()
        },
        {
          title: t('planTemplate.wizard.validationPreview.sections.scope.title'),
          icon: <ShopOutlined />,
          status: getValidationStatus(scopeValidation),
          errors: scopeValidation.errors,
          warnings: scopeValidation.warnings || [],
          details: renderScopeDetails()
        }
      ];

      setValidationSections(sections);

      // Update template validation status
      const validationStatus = finalResult.isValid ? 'Validation Passed' : 'Validation Failed';
      onDataChange({ validationStatus });

    } catch (error) {
      console.error('Validation error:', error);
      message.error(t('planTemplate.wizard.validationPreview.validation.validationError'));
    } finally {
      setLoading(false);
    }
  }, [templateData, onValidationChange, onDataChange]);

  // Get validation status for display
  const getValidationStatus = (result: ValidationResult): 'success' | 'error' | 'warning' | 'info' => {
    if (result.errors.length > 0) return 'error';
    if (result.warnings && result.warnings.length > 0) return 'warning';
    return 'success';
  };

  // Load plan previews
  const loadPlanPreviews = useCallback(async () => {
    // if (!templateData.id || !validationResult.isValid) return;

    // setPreviewLoading(true);
    // try {
    //   const response = await PlanogramTemplateService.previewGeneratedPlans(templateData.id);
    //   if (response.success) {
    //     setPlanPreviews(response.data);
    //   }
    // } catch (error) {
    //   console.error('Failed to load plan previews:', error);
    // } finally {
    //   setPreviewLoading(false);
    // }
  }, [templateData.id, validationResult.isValid]);

  // Render basic info details
  const renderBasicInfoDetails = () => (
    <Descriptions size="small" column={2}>
      <Descriptions.Item label={t('planTemplate.wizard.validationPreview.sections.basicInfo.templateCode')}>{templateData.code || '-'}</Descriptions.Item>
      <Descriptions.Item label={t('planTemplate.wizard.validationPreview.sections.basicInfo.templateName')}>{templateData.name || '-'}</Descriptions.Item>
      <Descriptions.Item label={t('planTemplate.wizard.validationPreview.sections.basicInfo.planType')}>{templateData.planType || '-'}</Descriptions.Item>
      {/* <Descriptions.Item label={t('planTemplate.wizard.validationPreview.sections.basicInfo.businessActivity')}>{templateData.businessActivity || '-'}</Descriptions.Item> */}
      <Descriptions.Item label={t('planTemplate.wizard.validationPreview.sections.basicInfo.description')} span={2}>
        {templateData.description || '-'}
      </Descriptions.Item>
    </Descriptions>
  );

  // Render milestone details
  const renderMilestoneDetails = () => {
    if (!templateData.milestones || templateData.milestones.length === 0) {
      return <Empty description={t('planTemplate.wizard.validationPreview.sections.milestone.notConfigured')} />;
    }

    const columns = [
      {
        title: t('planTemplate.wizard.validationPreview.sections.milestone.sequence'),
        dataIndex: 'sequence',
        key: 'sequence',
        width: 60,
        sorter: (a: MilestoneConfig, b: MilestoneConfig) => a.sequence - b.sequence
      },
      {
        title: t('planTemplate.wizard.validationPreview.sections.milestone.name'),
        dataIndex: 'name',
        key: 'name'
      },
      {
        title: t('planTemplate.wizard.validationPreview.sections.milestone.responsibleTeam'),
        dataIndex: 'responsibleTeam',
        key: 'responsibleTeam',
        render: (team: string) => <Tag color="blue">{team}</Tag>
      },
      {
        title: t('planTemplate.wizard.validationPreview.sections.milestone.estimatedDuration'),
        dataIndex: 'durationEstimate',
        key: 'durationEstimate',
        render: (days: number) => t('planTemplate.wizard.validationPreview.sections.milestone.days', { days })
      },
      {
        title: t('planTemplate.wizard.validationPreview.sections.milestone.bufferTime'),
        dataIndex: 'bufferTime',
        key: 'bufferTime',
        render: (days: number) => t('planTemplate.wizard.validationPreview.sections.milestone.days', { days })
      },
      {
        title: t('planTemplate.wizard.validationPreview.sections.milestone.parallelExecution'),
        dataIndex: 'parallelExecution',
        key: 'parallelExecution',
        render: (parallel: boolean) => (
          <Tag color={parallel ? 'green' : 'default'}>
            {parallel ? t('planTemplate.wizard.validationPreview.sections.milestone.yes') : t('planTemplate.wizard.validationPreview.sections.milestone.no')}
          </Tag>
        )
      }
    ];

    return (
      <Table
        dataSource={templateData.milestones}
        columns={columns}
        rowKey="id"
        size="small"
        pagination={false}
        scroll={{ y: 500 }}
      />
    );
  };

  // Render cycle details
  const renderCycleDetails = () => {
    if (!templateData.executionCycle) {
      return <Empty description={t('planTemplate.wizard.validationPreview.sections.cycle.notConfigured')} />;
    }

    const cycle = templateData.executionCycle;
    const weekdays = t('planTemplate.wizard.validationPreview.sections.cycle.weekdays', { returnObjects: true }) as string[];
    
    return (
      <Descriptions size="small" column={2}>
        <Descriptions.Item label={t('planTemplate.wizard.validationPreview.sections.cycle.frequencyType')}>{cycle.frequencyType}</Descriptions.Item>
        <Descriptions.Item label={t('planTemplate.wizard.validationPreview.sections.cycle.interval')}>{cycle.interval}</Descriptions.Item>
        <Descriptions.Item label={t('planTemplate.wizard.validationPreview.sections.cycle.firstExecutionDate')}>{formatDateTime(cycle.firstExecutionDate)}</Descriptions.Item>
        <Descriptions.Item label={t('planTemplate.wizard.validationPreview.sections.cycle.terminationType')}>{cycle.terminationType}</Descriptions.Item>
        {cycle.endDate && (
          <Descriptions.Item label={t('planTemplate.wizard.validationPreview.sections.cycle.endDate')}>{formatDateTime(cycle.endDate)}</Descriptions.Item>
        )}
        {cycle.repeatCount && (
          <Descriptions.Item label={t('planTemplate.wizard.validationPreview.sections.cycle.repeatCount')}>{cycle.repeatCount}</Descriptions.Item>
        )}
        {cycle.weekDaySelection && cycle.weekDaySelection.length > 0 && (
          <Descriptions.Item label={t('planTemplate.wizard.validationPreview.sections.cycle.selectedWeekdays')} span={2}>
            {cycle.weekDaySelection.map(day => weekdays[day]).join(', ')}
          </Descriptions.Item>
        )}
        {cycle.monthDaySelection && cycle.monthDaySelection.length > 0 && (
          <Descriptions.Item label={t('planTemplate.wizard.validationPreview.sections.cycle.selectedDates')} span={2}>
            {cycle.monthDaySelection.join(', ')}{t('planTemplate.wizard.validationPreview.sections.cycle.dayUnit')}
          </Descriptions.Item>
        )}
      </Descriptions>
    );
  };

  // Render scope details
  const renderScopeDetails = () => {
    if (!templateData.scopeDefinition) {
      return <Empty description={t('planTemplate.wizard.validationPreview.sections.scope.notConfigured')} />;
    }

    const scope = templateData.scopeDefinition;
    return (
      <Descriptions size="small" column={2}>
        <Descriptions.Item label={t('planTemplate.wizard.validationPreview.sections.scope.scopeType')}>{scope.scopeType}</Descriptions.Item>
        <Descriptions.Item label={t('planTemplate.wizard.validationPreview.sections.scope.affectedStores')}>{scope.storeCount}</Descriptions.Item>
        {scope.storeGroupList && scope.storeGroupList.length > 0 && (
          <Descriptions.Item label={t('planTemplate.wizard.validationPreview.sections.scope.storeGroups')} span={2}>
            {t('planTemplate.wizard.validationPreview.sections.scope.storeGroupCount', { count: scope.storeGroupList.length })}
          </Descriptions.Item>
        )}
        {scope.customStoreList && scope.customStoreList.length > 0 && (
          <Descriptions.Item label={t('planTemplate.wizard.validationPreview.sections.scope.customStores')} span={2}>
            {t('planTemplate.wizard.validationPreview.sections.scope.storeCount', { count: scope.customStoreList.length })}
          </Descriptions.Item>
        )}
        {scope.excludedStores && scope.excludedStores.length > 0 && (
          <Descriptions.Item label={t('planTemplate.wizard.validationPreview.sections.scope.excludedStores')} span={2}>
            {t('planTemplate.wizard.validationPreview.sections.scope.storeCount', { count: scope.excludedStores.length })}
          </Descriptions.Item>
        )}
      </Descriptions>
    );
  };

  // Run validation on mount and when template data changes
  useEffect(() => {
    performValidation();
  }, [performValidation]);

  const formatDateTime = (timestamp: number): string => {
    if (!timestamp) {
      return '-';
    }
    return dayjs(timestamp).format('YYYY-MM-DD');
  };
  
  // Load plan previews when validation passes
  useEffect(() => {
    if (validationResult.isValid) {
      loadPlanPreviews();
    }
  }, [validationResult.isValid, loadPlanPreviews]);

  return (
    <div style={{ padding: '24px' }}>
      <Title level={3}>{t('planTemplate.wizard.validationPreview.title')}</Title>
      <Paragraph type="secondary">
        {t('planTemplate.wizard.validationPreview.description')}
      </Paragraph>

      <Spin spinning={loading}>
        {/* Validation Summary */}
        <Card style={{ marginBottom: 24 }}>
          <Row gutter={16}>
            <Col span={6}>
              <Statistic
                title={t('planTemplate.wizard.validationPreview.validation.status')}
                value={validationResult.isValid ? t('planTemplate.wizard.validationPreview.validation.passed') : t('planTemplate.wizard.validationPreview.validation.failed')}
                valueStyle={{ 
                  color: validationResult.isValid ? '#3f8600' : '#cf1322' 
                }}
                prefix={validationResult.isValid ? 
                  <CheckCircleOutlined /> : 
                  <ExclamationCircleOutlined />
                }
              />
            </Col>
            <Col span={6}>
              <Statistic
                title={t('planTemplate.wizard.validationPreview.validation.errors')}
                value={validationResult.errors.length}
                valueStyle={{ color: validationResult.errors.length > 0 ? '#cf1322' : '#3f8600' }}
                prefix={<WarningOutlined />}
              />
            </Col>
            <Col span={6}>
              <Statistic
                title={t('planTemplate.wizard.validationPreview.validation.warnings')}
                value={validationResult.warnings?.length || 0}
                valueStyle={{ color: '#fa8c16' }}
                prefix={<InfoCircleOutlined />}
              />
            </Col>
            <Col span={6}>
              <Statistic
                title={t('planTemplate.wizard.validationPreview.validation.items')}
                value={validationSections.length}
                prefix={<CheckCircleOutlined />}
              />
            </Col>
          </Row>

          {validationResult.isValid && (
            <Alert
              message={t('planTemplate.wizard.validationPreview.validation.validationPassed')}
              description={t('planTemplate.wizard.validationPreview.validation.validationPassedDesc')}
              type="success"
              showIcon
              style={{ marginTop: 16 }}
            />
          )}

          {!validationResult.isValid && (
            <Alert
              message={t('planTemplate.wizard.validationPreview.validation.validationFailed')}
              description={t('planTemplate.wizard.validationPreview.validation.validationFailedDesc', { count: validationResult.errors.length })}
              type="error"
              showIcon
              style={{ marginTop: 16 }}
            />
          )}
        </Card>

        {/* Validation Details */}
        <Card title={t('planTemplate.wizard.validationPreview.validation.title')} style={{ marginBottom: 24 }}>
          <Collapse>
            {validationSections.map((section, index) => (
              <Panel
                key={index}
                header={
                  <Space>
                    {section.icon}
                    <span>{section.title}</span>
                    {section.status === 'success' && <Tag color="success">{t('planTemplate.wizard.validationPreview.validation.passed')}</Tag>}
                    {section.status === 'error' && <Tag color="error">{t('planTemplate.wizard.validationPreview.validation.failed')}</Tag>}
                    {section.status === 'warning' && <Tag color="warning">{t('planTemplate.wizard.validationPreview.validation.warning')}</Tag>}
                    {section.errors.length > 0 && (
                      <Tag color="red">{t('planTemplate.wizard.validationPreview.validation.errorCount', { count: section.errors.length })}</Tag>
                    )}
                    {section.warnings.length > 0 && (
                      <Tag color="orange">{t('planTemplate.wizard.validationPreview.validation.warningCount', { count: section.warnings.length })}</Tag>
                    )}
                  </Space>
                }
              >
                {/* Show errors */}
                {section.errors.length > 0 && (
                  <Alert
                    message={t('planTemplate.wizard.validationPreview.validation.foundErrors')}
                    description={
                      <ul style={{ margin: 0, paddingLeft: 20 }}>
                        {section.errors.map((error, idx) => (
                          <li key={idx}>{error.message}</li>
                        ))}
                      </ul>
                    }
                    type="error"
                    showIcon
                    style={{ marginBottom: 16 }}
                  />
                )}

                {/* Show warnings */}
                {section.warnings.length > 0 && (
                  <Alert
                    message={t('planTemplate.wizard.validationPreview.validation.attention')}
                    description={
                      <ul style={{ margin: 0, paddingLeft: 20 }}>
                        {section.warnings.map((warning, idx) => (
                          <li key={idx}>{warning.message}</li>
                        ))}
                      </ul>
                    }
                    type="warning"
                    showIcon
                    style={{ marginBottom: 16 }}
                  />
                )}

                {/* Show details */}
                {section.details}
              </Panel>
            ))}
          </Collapse>
        </Card>
      </Spin>

      <Divider />

      {/* Navigation Buttons */}
      <div style={{ display: 'flex', justifyContent: 'center' }}>
        <Button onClick={performValidation} loading={loading}>
          {t('planTemplate.wizard.validationPreview.validation.revalidate')}
        </Button>
      </div>
    </div>
  );
};

export default ValidationPreview;